import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useSalesforce } from '../hooks/useSalesforce'
import Navbar from '../components/Navbar'
import { supabase } from '../services/supabaseClient'
import { callEdgeFunction, isEdgeFunctionMigrated, getEdgeFunctionName } from '../lib/edgeFunctionClient'

// Pre-defined field sets for testing
const FIELD_SETS = {
  minimal: 'Id,MasterLabel,ProcessType,Status',
  basic: 'Id,MasterLabel,ProcessType,Status,Description,LastModifiedDate,DeveloperName,CreatedDate',
  extended: 'Id,MasterLabel,ProcessType,Status,Description,LastModifiedDate,DeveloperName,CreatedDate,LastModifiedById,CreatedById,NamespacePrefix,ManageableState',
  relationships: 'Id,MasterLabel,ProcessType,Status,LastModifiedBy.Name,CreatedBy.Name,ActiveVersion.Id',
  advanced: 'Id,MasterLabel,ProcessType,Status,Description,LastModifiedDate,DeveloperName,CreatedDate,LastModifiedById,CreatedById,NamespacePrefix,ManageableState,ActiveVersionId,Builder,IsTemplate,IsActive,ApiVersionRuntime'
};

export default function FieldTestPage() {
  const { user, signOut } = useAuth()
  const { orgs } = useSalesforce()
  const [searchParams] = useSearchParams()
  const [selectedOrgId, setSelectedOrgId] = useState<string | null>(null)
  const [fieldSet, setFieldSet] = useState<string>(FIELD_SETS.minimal)
  const [customFieldSet, setCustomFieldSet] = useState<string>('')
  const [apiVersion, setApiVersion] = useState<string>('57.0')
  const [testResult, setTestResult] = useState<any>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)
  const navigate = useNavigate()

  // Set the selected org from URL params or default to first org
  useEffect(() => {
    const orgId = searchParams.get('orgId')
    if (orgId) {
      setSelectedOrgId(orgId)
    } else if (orgs.length > 0 && !selectedOrgId) {
      setSelectedOrgId(orgs[0].id)
    }
  }, [searchParams, orgs, selectedOrgId])

  // Handle org change in dropdown
  const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newOrgId = e.target.value
    setSelectedOrgId(newOrgId)
    navigate(`/field-test?orgId=${newOrgId}`)
  }

  // Handle field set change
  const handleFieldSetChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedValue = e.target.value;
    
    if (selectedValue === 'custom') {
      // If custom, keep the current field set in the custom field input
      setCustomFieldSet(fieldSet);
    } else {
      // Set from predefined field sets
      setFieldSet(FIELD_SETS[selectedValue as keyof typeof FIELD_SETS]);
    }
  }

  // Handle custom field set change
  const handleCustomFieldSetChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setCustomFieldSet(e.target.value);
    setFieldSet(e.target.value);
  }

  // Handle API version change
  const handleApiVersionChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setApiVersion(e.target.value)
  }

  // Run the test
  const handleRunTest = async () => {
    if (!selectedOrgId) return;
    
    setIsLoading(true);
    setError(null);
    setTestResult(null);
    
    try {
      let result;
      const endpoint = '/api/salesforce/test-field-availability';
      
      if (isEdgeFunctionMigrated(endpoint)) {
        const functionName = getEdgeFunctionName(endpoint);
        if (functionName) {
          result = await callEdgeFunction(functionName, {
            orgId: selectedOrgId,
            fieldSet: fieldSet,
            apiVersion: apiVersion
          });
        }
      } else {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            orgId: selectedOrgId,
            fieldSet: fieldSet,
            apiVersion: apiVersion
          })
        });
        
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.message || 'Error testing field availability');
        }
        
        result = await response.json();
      }
      
      setTestResult(result);
    } catch (err) {
      console.error('Error testing field availability:', err);
      setError(err instanceof Error ? err : new Error(String(err)));
    } finally {
      setIsLoading(false);
    }
  }

  // Determine field set name for display
  const getFieldSetName = () => {
    for (const [key, value] of Object.entries(FIELD_SETS)) {
      if (value === fieldSet) {
        return key;
      }
    }
    return 'custom';
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Navbar onSignOut={signOut} />
      
      <div className="py-10">
        <header>
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center">
              <h1 className="text-3xl font-bold leading-tight text-gray-900">Salesforce Field Availability Test</h1>
              <div className="flex items-center space-x-2">
                {orgs.length > 0 ? (
                  <div className="relative">
                    <select
                      value={selectedOrgId || ''}
                      onChange={handleOrgChange}
                      className="block w-48 pl-4 pr-10 py-2 text-base border border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md"
                    >
                      <option value="">Select an organization</option>
                      {orgs.map(org => (
                        <option key={org.id} value={org.id}>{org.name}</option>
                      ))}
                    </select>
                    <div className="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
                      <svg className="h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                      </svg>
                    </div>
                  </div>
                ) : (
                  <button
                    onClick={() => navigate('/orgs')}
                    className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                  >
                    + Add Organization
                  </button>
                )}
              </div>
            </div>
          </div>
        </header>
        
        <main>
          <div className="max-w-7xl mx-auto sm:px-6 lg:px-8">
            <div className="px-4 py-8 sm:px-0">
              <div className="bg-white shadow-md px-4 py-5 sm:rounded-lg sm:p-6 mb-6">
                <div className="md:grid md:grid-cols-2 md:gap-6">
                  <div className="md:col-span-1">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Field Test Configuration</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      Test Salesforce field availability before modifying the API queries.
                    </p>
                    
                    <div className="mt-6 grid grid-cols-1 gap-y-6 gap-x-4 sm:grid-cols-6">
                      {/* Field Set Selection */}
                      <div className="sm:col-span-6">
                        <label htmlFor="field-set" className="block text-sm font-medium text-gray-700">
                          Field Set
                        </label>
                        <div className="mt-1">
                          <select
                            id="field-set"
                            value={getFieldSetName()}
                            onChange={handleFieldSetChange}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          >
                            <option value="minimal">Minimal (Base Fields)</option>
                            <option value="basic">Basic (+ Description, Dates, DeveloperName)</option>
                            <option value="extended">Extended (+ Creator, Modifier, Namespace)</option>
                            <option value="relationships">Relationships (LastModifiedBy, CreatedBy)</option>
                            <option value="advanced">Advanced (All Available Fields)</option>
                            <option value="custom">Custom Field Set</option>
                          </select>
                        </div>
                      </div>
                      
                      {/* Custom Field Set Input */}
                      {getFieldSetName() === 'custom' && (
                        <div className="sm:col-span-6">
                          <label htmlFor="custom-fields" className="block text-sm font-medium text-gray-700">
                            Custom Fields (Comma Separated)
                          </label>
                          <div className="mt-1">
                            <textarea
                              id="custom-fields"
                              value={customFieldSet}
                              onChange={handleCustomFieldSetChange}
                              rows={3}
                              className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                              placeholder="Id,Name,Status,etc."
                            />
                          </div>
                        </div>
                      )}
                      
                      {/* API Version Input */}
                      <div className="sm:col-span-3">
                        <label htmlFor="api-version" className="block text-sm font-medium text-gray-700">
                          API Version
                        </label>
                        <div className="mt-1">
                          <input
                            type="text"
                            id="api-version"
                            value={apiVersion}
                            onChange={handleApiVersionChange}
                            className="shadow-sm focus:ring-blue-500 focus:border-blue-500 block w-full sm:text-sm border-gray-300 rounded-md"
                          />
                        </div>
                      </div>
                      
                      {/* Fields Preview */}
                      <div className="sm:col-span-6">
                        <label className="block text-sm font-medium text-gray-700">
                          Fields to Test
                        </label>
                        <div className="mt-1">
                          <div className="bg-gray-50 p-3 rounded-md text-sm text-gray-700 font-mono overflow-auto">
                            {fieldSet}
                          </div>
                        </div>
                      </div>
                      
                      {/* Run Test Button */}
                      <div className="sm:col-span-6">
                        <button
                          onClick={handleRunTest}
                          disabled={!selectedOrgId || isLoading}
                          className={`inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white ${
                            !selectedOrgId || isLoading
                              ? 'bg-gray-400 cursor-not-allowed'
                              : 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                          }`}
                        >
                          {isLoading ? 'Testing...' : 'Run Test'}
                        </button>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-5 md:mt-0 md:col-span-1">
                    <h3 className="text-lg font-medium leading-6 text-gray-900">Test Results</h3>
                    
                    {isLoading ? (
                      <div className="mt-4 flex justify-center">
                        <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-blue-500"></div>
                      </div>
                    ) : error ? (
                      <div className="mt-4 bg-red-50 p-4 rounded-md">
                        <div className="flex">
                          <div className="flex-shrink-0">
                            <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                            </svg>
                          </div>
                          <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <div className="mt-2 text-sm text-red-700">
                              <p>{error.message}</p>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : testResult ? (
                      <div className="mt-4">
                        {/* Overall Status */}
                        <div className={`p-4 rounded-md ${testResult.success ? 'bg-green-50' : 'bg-yellow-50'}`}>
                          <div className="flex">
                            <div className="flex-shrink-0">
                              {testResult.success ? (
                                <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                                </svg>
                              ) : (
                                <svg className="h-5 w-5 text-yellow-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                                </svg>
                              )}
                            </div>
                            <div className="ml-3">
                              <h3 className={`text-sm font-medium ${testResult.success ? 'text-green-800' : 'text-yellow-800'}`}>
                                {testResult.success ? 'Success' : 'Warning/Failure'}
                              </h3>
                              <div className={`mt-2 text-sm ${testResult.success ? 'text-green-700' : 'text-yellow-700'}`}>
                                <p>
                                  {testResult.success
                                    ? `Retrieved ${testResult.recordCount} flow(s).`
                                    : testResult.error || 'Query failed with unknown error.'}
                                </p>
                                {testResult.standardQuery && testResult.standardQuery.success && (
                                  <p className="mt-1">
                                    Standard API query succeeded as fallback with {testResult.standardQuery.recordCount} flow(s).
                                  </p>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                        
                        {/* Success Rate */}
                        {testResult.successRate && (
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-700">Field Availability</h4>
                            <div className="mt-2 flex items-center">
                              <div className="flex-1 bg-gray-200 rounded-full h-2.5">
                                <div 
                                  className={`h-2.5 rounded-full ${
                                    testResult.successRate.percentage > 80 ? 'bg-green-500' :
                                    testResult.successRate.percentage > 50 ? 'bg-yellow-500' :
                                    'bg-red-500'
                                  }`} 
                                  style={{ width: `${testResult.successRate.percentage}%` }}
                                ></div>
                              </div>
                              <span className="ml-3 text-sm text-gray-700">
                                {testResult.successRate.available}/{testResult.successRate.requested} fields ({testResult.successRate.percentage}%)
                              </span>
                            </div>
                          </div>
                        )}
                        
                        {/* Field Details */}
                        {testResult.fieldAvailability && (
                          <div className="mt-4">
                            <h4 className="text-sm font-medium text-gray-700">Field Details</h4>
                            <div className="mt-2 overflow-auto max-h-96 border border-gray-200 rounded-md">
                              <table className="min-w-full divide-y divide-gray-200">
                                <thead className="bg-gray-50">
                                  <tr>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Field Name</th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Available</th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Type</th>
                                    <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sample</th>
                                  </tr>
                                </thead>
                                <tbody className="bg-white divide-y divide-gray-200">
                                  {Object.entries(testResult.fieldAvailability).map(([field, info]: [string, any]) => (
                                    <tr key={field}>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900">{field}</td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">
                                        {info.available ? (
                                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-green-100 text-green-800">
                                            Yes
                                          </span>
                                        ) : (
                                          <span className="px-2 inline-flex text-xs leading-5 font-semibold rounded-full bg-red-100 text-red-800">
                                            No
                                          </span>
                                        )}
                                      </td>
                                      <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500">{info.type}</td>
                                      <td className="px-3 py-2 text-sm text-gray-500 max-w-xs truncate">{info.sample}</td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        )}
                        
                        {/* Performance Info */}
                        <div className="mt-4">
                          <h4 className="text-sm font-medium text-gray-700">Performance</h4>
                          <p className="text-sm text-gray-500">
                            Response Time: {testResult.queryDetails?.responseTimeMs}ms
                          </p>
                        </div>
                        
                        {/* Raw Response */}
                        <div className="mt-4">
                          <details>
                            <summary className="text-sm font-medium text-gray-700 cursor-pointer">
                              View Raw Response
                            </summary>
                            <div className="mt-2 bg-gray-50 p-3 rounded-md">
                              <pre className="text-xs overflow-auto max-h-96 text-gray-700">
                                {JSON.stringify(testResult, null, 2)}
                              </pre>
                            </div>
                          </details>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-4 p-4 bg-gray-50 rounded-md">
                        <p className="text-sm text-gray-500">
                          Select an organization and run a test to see results.
                        </p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    </div>
  )
}